import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Post, User } from '../types';
import HeartFilledIcon from './icons/HeartFilledIcon';

interface Props {
  post: Post;
  usersById: Record<string, User>;
  onViewProfile: (user: User) => void;
  onClose: () => void;
}

interface Person {
  id: string;
  user: User | null;
  liked: boolean;
  reaction: string | null;
}

// Everyone who liked and/or reacted to a post, with All / Likes / per-emoji tabs.
const PostReactionsSheet: React.FC<Props> = ({ post, usersById, onViewProfile, onClose }) => {
  const [tab, setTab] = useState<string>('all');

  const people = useMemo<Person[]>(() => {
    const ids = new Set<string>([...(post.likedBy || []), ...Object.keys(post.reactions || {})]);
    return Array.from(ids).map((id) => ({
      id,
      user: usersById[id] || null,
      liked: (post.likedBy || []).includes(id),
      reaction: (post.reactions || {})[id] || null,
    }));
  }, [post.likedBy, post.reactions, usersById]);

  const emojiCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    people.forEach((p) => { if (p.reaction) counts[p.reaction] = (counts[p.reaction] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [people]);

  const likeCount = people.filter((p) => p.liked).length;
  const shown = people.filter((p) => (tab === 'all' ? true : tab === 'likes' ? p.liked : p.reaction === tab));

  const tabClass = (active: boolean) =>
    `shrink-0 rounded-full px-3.5 py-1.5 text-sm font-bold transition ${active ? 'bg-white text-black' : 'bg-white/[0.08] text-gray-300 hover:bg-white/15'}`;

  return createPortal(
    <div className="fixed inset-0 z-[1400] flex items-end justify-center bg-black/70 sm:items-center" onClick={onClose}>
      <div
        role="dialog"
        aria-label="Likes and reactions"
        className="flex max-h-[80vh] w-full max-w-[560px] flex-col rounded-t-3xl bg-[#0b0b0b] p-5 text-white sm:rounded-3xl"
        style={{ paddingBottom: 'calc(var(--sab-native, env(safe-area-inset-bottom)) + 1.25rem)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-black">Likes &amp; reactions</h2>
          <button type="button" onClick={onClose} className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-bold text-gray-200 hover:bg-white/20">Close</button>
        </div>

        <div className="mb-3 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <button type="button" onClick={() => setTab('all')} className={tabClass(tab === 'all')}>All {people.length}</button>
          {likeCount > 0 && <button type="button" onClick={() => setTab('likes')} className={tabClass(tab === 'likes')}>♥ {likeCount}</button>}
          {emojiCounts.map(([emoji, n]) => (
            <button key={emoji} type="button" onClick={() => setTab(emoji)} className={tabClass(tab === emoji)}>{emoji} {n}</button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {shown.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">No one yet.</p>
          ) : (
            <ul className="space-y-1">
              {shown.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    disabled={!p.user}
                    onClick={() => { if (p.user) { onViewProfile(p.user); onClose(); } }}
                    className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition hover:bg-white/[0.06] disabled:cursor-default"
                  >
                    <img src={p.user?.avatarUrl || '/logo.png'} alt="" className="h-11 w-11 shrink-0 rounded-xl object-cover" />
                    <span className="min-w-0 flex-1 truncate text-sm font-bold">{'@' + String(p.user?.username || 'someone').toUpperCase()}</span>
                    <span className="flex shrink-0 items-center gap-1.5 text-xl leading-none">
                      {p.liked && <HeartFilledIcon className="h-5 w-5 text-white" />}
                      {p.reaction && <span>{p.reaction}</span>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PostReactionsSheet;
